import { useEffect, useMemo, useRef, useState } from 'react';

export const PARSE_STATE = {
    QUEUED: 'queued',
    CONVERTING: 'converting',
    EXTRACTING: 'extracting',
    APPLYING: 'applying',
    COMPLETED: 'completed',
    FAILED: 'failed',
};

const TERMINAL = [PARSE_STATE.COMPLETED, PARSE_STATE.FAILED];
const POLL_MS = 5000;

const DEV_OPEN_ON_UPLOAD = false;

export const SLOW_MS = 7 * 60 * 1000;

const useExtractionGate = ({
    applicationForm,
    allStartUploaded,
    hasStartRequirements = true,
    requirementsKnown = true,
    allStartRead = false,
    onPoll,
    enabled = true,
    parseState,
}) => {
    const [polling, setPolling] = useState(false);
    const [slow, setSlow] = useState(false);
    const pollRef = useRef(null);
    const onPollRef = useRef(onPoll);

    useEffect(() => {
        onPollRef.current = onPoll;
    }, [onPoll]);

    const extractionDispatched = applicationForm?.startExtractionDone === true;

    const persistedState = applicationForm?.startExtractionState;
    const extractionDone = TERMINAL.includes(persistedState) || TERMINAL.includes(parseState);

    const noStartWorkToDo = requirementsKnown
        && allStartUploaded && !hasStartRequirements && !extractionDispatched;

    // Per-document "read" state is only a gate-open signal when NO AI extraction was
    // dispatched. Once an extraction run has been dispatched, the authoritative "fields
    // are extracted" signal is the run reaching a terminal state (extractionDone), so the
    // gate must not shortcut to open while that run is still pending — otherwise Continue
    // enables even though the extraction is still "requested" and 0 fields were pre-filled.
    const openOnRead = requirementsKnown && hasStartRequirements
        && allStartUploaded && allStartRead && !extractionDispatched;

    const openOnUpload = DEV_OPEN_ON_UPLOAD && requirementsKnown && hasStartRequirements
        && allStartUploaded;

    // A required start document removed after extraction finished closes the gate again
    // until it is re-uploaded.
    const waitingForUploads = enabled && !allStartUploaded;
    const waitingForParse = enabled && !waitingForUploads && !extractionDone && !noStartWorkToDo
        && !openOnRead && !openOnUpload
        && ((allStartUploaded && requirementsKnown) || extractionDispatched);
    const gateOpen = !enabled || noStartWorkToDo || openOnRead || openOnUpload
        || (extractionDone && allStartUploaded);

    useEffect(() => {
        const shouldPoll = waitingForParse;
        setPolling(shouldPoll);
        if (!shouldPoll) {
            setSlow(false);
            if (pollRef.current) {
                clearInterval(pollRef.current);
                pollRef.current = null;
            }
            return undefined;
        }
        const startedAt = Date.now();
        pollRef.current = setInterval(() => {
            onPollRef.current?.();
            if (Date.now() - startedAt > SLOW_MS) setSlow(true);
        }, POLL_MS);
        return () => {
            if (pollRef.current) {
                clearInterval(pollRef.current);
                pollRef.current = null;
            }
        };
    }, [waitingForParse]);

    const blockedReason = useMemo(() => {
        if (gateOpen) return null;
        if (waitingForUploads) return 'uploads';
        if (waitingForParse) return 'parse';
        return null;
    }, [gateOpen, waitingForUploads, waitingForParse]);

    return {
        gateOpen,
        openedOnRead: openOnRead && !extractionDone,
        extractionDispatched,
        persistedParseState: persistedState || null,
        persistedParseError: applicationForm?.startExtractionError || null,
        waitingForUploads,
        waitingForParse,
        blockedReason,
        polling,
        slow,
    };
};

export default useExtractionGate;

